import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { quantileLabel } from '@/lib/quantiles';
import { aneelCaseParams, fetchAneelCollection } from '@/services/aneel';
import type { AneelLateness, AneelSuspendedCollection, AnswerOut } from '@/services/types';

/** The same floor the backend draws its ladders above. */
const LADDER_FLOOR = 20;

/**
 * The case list filtered to one cell of this screen. Its `credit_status`
 * filter is an `exists` at case grain and its `trajectory` filter folds a
 * null into UNCLASSIFIED, so the linked total is the cell's CASE count --
 * never its row count -- and the two are one predicate.
 */
function casesHref(filters: { trajectory?: string; credit_status?: string }): string {
  return `/aneel/cases?${aneelCaseParams(filters).toString()}`;
}

/**
 * What became of the fines ANEEL billed — and what that cannot tell you.
 *
 * The screen exists for one measurement: 227 cases with no recorded penalty
 * outcome are already settled in full. Billing and adjudication are separate
 * ANEEL systems that do not wait for each other, and a reader who assumes
 * otherwise reads every cell of the cross-tab as a contradiction.
 *
 * The figure a reader most wants — how much of what ANEEL imposes it actually
 * collects — is refused, on the merits rather than for want of data. Billed and
 * imposed are different quantities from different systems, and the billed one
 * accrues interest over time. Dividing them produces something that looks like a
 * recovery rate and is not one.
 *
 * Every count of CASES on this screen opens the case list filtered to exactly
 * those cases: the cross-tab cells, the credit-state rows, the headline of
 * settled-but-undecided, the stayed states. A figure a reader can click through
 * to its rows is a figure they can check.
 */
export function AneelCollectionPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'collection'],
    queryFn: fetchAneelCollection,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:collection.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:collection.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:collection.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/collection" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* Before any number. Every figure below is read wrongly by a reader who
          assumes billing follows adjudication, and by the time they reach a
          footnote they have already read the cross-tab as a list of errors. */}
      <p
        role="alert"
        className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
      >
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>{t('aneel:collection.neverConflate')}</span>
      </p>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:collection.reach.title')}
        </h2>
        <Answer answer={data.reach} help={t('aneel:collection.reach.help')}>
          {(reach) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {(
                  [
                    ['linkedCases', reach.linked_cases],
                    ['linkedRows', reach.linked_rows],
                    ['unmatchedRows', reach.unmatched_rows],
                    ['ambiguousRows', reach.ambiguous_rows],
                    ['totalRows', reach.total_rows],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-background p-3">
                    <dt className="text-xs text-muted-foreground">
                      {t(`aneel:collection.reach.${key}`)}
                    </dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </dd>
                  </div>
                ))}
              </dl>
              {/* Both directions in one sentence. Either alone flatters: one
                  reads as good coverage, the other as broken data. */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:collection.reach.bothWays', {
                  cases: number.format(reach.linked_cases),
                  total: number.format(reach.total_cases),
                  caseShare: percent.format(reach.case_coverage),
                  rows: number.format(reach.linked_rows),
                  totalRows: number.format(reach.total_rows),
                  rowShare: percent.format(reach.row_coverage),
                })}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:collection.guide.reach" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:collection.crossTab.title')}
        </h2>
        <Answer answer={data.cross_tab} help={t('aneel:collection.crossTab.help')}>
          {(table) => (
            <div className="space-y-3">
              {/* The headline is the contradiction, not the agreement. The
                  agreement is what a reader already assumes. */}
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">
                {t('aneel:collection.crossTab.settledButUndecided', {
                  n: number.format(table.settled_but_undecided),
                })}{' '}
                <Link
                  to={casesHref({ trajectory: 'PENDING_DECISION', credit_status: 'Quitada' })}
                  className="text-primary underline-offset-2 hover:underline"
                  data-settled-undecided-link
                >
                  {t('aneel:collection.crossTab.openSettledUndecided')}
                </Link>
              </p>

              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('aneel:collection.crossTab.outcome')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('aneel:collection.crossTab.credit')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:collection.crossTab.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:collection.crossTab.cases')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.cells.map((cell) => {
                      const outcome = t(
                        `aneel:overview.trajectories.${cell.trajectory}`,
                        cell.trajectory,
                      );
                      return (
                        <tr
                          key={`${cell.trajectory}-${cell.credit_status}`}
                          className="border-t"
                          data-cell={`${cell.trajectory}|${cell.credit_status}`}
                          data-cases={cell.cases}
                        >
                          <td className="px-3 py-2 text-foreground">{outcome}</td>
                          <td className="px-3 py-2 text-muted-foreground">{cell.credit_status}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {number.format(cell.rows)}
                          </td>
                          {/* The CASE count is the link, because that is what
                              the list opens: an `exists` at case grain. A
                              null credit state has no filter and stays text. */}
                          <td className="px-3 py-2 text-right tabular-nums text-foreground">
                            {cell.credit_status === '(none)' ? (
                              number.format(cell.cases)
                            ) : (
                              <Link
                                to={casesHref({
                                  trajectory: cell.trajectory,
                                  credit_status: cell.credit_status,
                                })}
                                className="text-primary underline-offset-2 hover:underline"
                                aria-label={t('aneel:collection.crossTab.openCases', {
                                  n: number.format(cell.cases),
                                  outcome,
                                  credit: cell.credit_status,
                                })}
                              >
                                {number.format(cell.cases)}
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Said where the column is, not in a caption. A reader who adds
                  the case column gets 474 over 459 cases and concludes the table
                  is wrong; it is not, and this says why. */}
              {table.double_counted > 0 ? (
                <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                  {t('aneel:collection.crossTab.doesNotSum', {
                    sum: number.format(table.case_cells_total),
                    cases: number.format(table.linked_cases),
                    overlap: number.format(table.double_counted),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:collection.guide.crossTab" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-foreground">
          {t('aneel:collection.mix.title')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('aneel:collection.mix.help')}</p>
        <ul className="space-y-1">
          {data.credit_mix.map((share) => (
            <li
              key={share.code}
              className="flex flex-wrap items-baseline justify-between gap-3 border-b pb-1 text-sm last:border-b-0"
              data-mix-status={share.code}
              data-cases={share.cases}
            >
              {/* `(none)` is the SQL coalesce placeholder for a null status.
                  Rendered raw it reads as a category ANEEL uses rather than as
                  an absent value, and does so in English. */}
              <span className="text-foreground">
                {share.code === '(none)' ? t('aneel:fees.notRecorded') : share.code}
              </span>
              <span className="flex items-baseline gap-3 tabular-nums text-muted-foreground">
                <span>
                  {number.format(share.count)} · {percent.format(share.share)}
                </span>
                {/* Rows are the grain; the cases are what the list opens. */}
                {share.code === '(none)' ? (
                  <span>{t('aneel:collection.mix.cases', { n: number.format(share.cases) })}</span>
                ) : (
                  <Link
                    to={casesHref({ credit_status: share.code })}
                    className="text-primary underline-offset-2 hover:underline"
                    aria-label={t('aneel:collection.mix.open', {
                      n: number.format(share.cases),
                      status: share.code,
                    })}
                  >
                    {t('aneel:collection.mix.cases', { n: number.format(share.cases) })}
                  </Link>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <LatenessPanel answer={data.lateness} />
        <SuspendedPanel answer={data.suspended} />
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:collection.money.title')}
        </h2>
        <dl className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-md border bg-background p-3">
            <dt className="text-xs text-muted-foreground">{t('aneel:collection.money.billed')}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
              {currency.format(data.billed_brl)}
            </dd>
          </div>
          <div className="rounded-md border bg-background p-3">
            <dt className="text-xs text-muted-foreground">{t('aneel:collection.money.paid')}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
              {currency.format(data.paid_brl)}
            </dd>
          </div>
        </dl>
        {/* Both totals are shown and the division between them is REFUSED, right
            underneath. Showing the two numbers without the refusal would be an
            invitation to compute exactly the figure this corpus cannot support. */}
        <Answer
          answer={data.recovery}
          help={t('aneel:collection.money.help')}
          elsewhere={t('aneel:collection.money.elsewhere')}
        >
          {() => null}
        </Answer>
      </section>
    </div>
  );
}

/**
 * How late the paid instalments were paid. Over PAID rows only -- lateness is
 * the source's arithmetic between due and payment dates, so an unpaid
 * instalment has none yet -- which is why this can never say what is overdue.
 */
function LatenessPanel({ answer }: { answer: AnswerOut<AneelLateness> }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-lateness>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('aneel:collection.lateness.title')}
      </h2>
      <Answer answer={answer} help={t('aneel:collection.lateness.help')}>
        {(late) => (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-3">
              {(
                [
                  ['paidRows', late.paid_rows],
                  ['lateRows', late.late_rows],
                  ['onTimeRows', late.on_time_rows],
                  ['lateCases', late.late_cases],
                ] as const
              ).map(([key, value]) => (
                <div key={key} className="rounded-md border bg-background p-3">
                  <dt className="text-xs text-muted-foreground">
                    {t(`aneel:collection.lateness.${key}`)}
                  </dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                    {number.format(value)}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground" data-late-share={late.late_rows}>
              {t('aneel:collection.lateness.lateShare', {
                late: number.format(late.late_rows),
                paid: number.format(late.paid_rows),
                share: percent.format(late.late_share),
                lateCases: number.format(late.late_cases),
                paidCases: number.format(late.paid_cases),
              })}
            </p>
            {late.late_days ? (
              <div className="rounded-md border bg-background p-3" data-late-ladder>
                <h3 className="mb-1 text-xs font-medium text-foreground">
                  {t('aneel:collection.lateness.ladder')}
                </h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t('aneel:collection.lateness.observations', {
                    n: number.format(late.late_days.n),
                  })}
                </p>
                <dl className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                  {Object.entries(late.late_days.quantiles).map(([key, value]) => (
                    <div key={key}>
                      <dt className="text-xs text-muted-foreground">{quantileLabel(t, key)}</dt>
                      <dd className="text-sm tabular-nums text-foreground">
                        {number.format(Math.round(value))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : late.late_rows > 0 ? (
              <p className="text-xs text-muted-foreground" data-late-floor>
                {t('aneel:collection.lateness.floor', { minimum: LADDER_FLOOR })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="aneel:collection.guide.lateness" />
      </div>
    </section>
  );
}

/**
 * Billing stayed by a guarantee, a judicial deposit or a court decision. A
 * state of the collection, never a verdict on the notice; each state opens
 * the cases behind it, at the count the list will show.
 */
function SuspendedPanel({ answer }: { answer: AnswerOut<AneelSuspendedCollection> }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-suspended>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('aneel:collection.suspended.title')}
      </h2>
      <Answer answer={answer} help={t('aneel:collection.suspended.help')}>
        {(stayed) => (
          <div className="space-y-3">
            <p
              className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground"
              data-suspended-cases={stayed.cases}
            >
              {t('aneel:collection.suspended.headline', {
                cases: number.format(stayed.cases),
                linked: number.format(stayed.linked_cases),
                share: percent.format(stayed.case_share),
              })}
            </p>
            {stayed.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('aneel:collection.suspended.none')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t('aneel:collection.suspended.status')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:collection.suspended.rows')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:collection.suspended.cases')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stayed.rows.map((row) => (
                    <tr
                      key={row.status}
                      className="border-t"
                      data-suspended-status={row.status}
                      data-cases={row.cases}
                    >
                      <td className="px-3 py-2 text-foreground">{row.status}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.rows)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={casesHref({ credit_status: row.status })}
                          className="text-primary underline-offset-2 hover:underline"
                          aria-label={t('aneel:collection.suspended.open', {
                            n: number.format(row.cases),
                            status: row.status,
                          })}
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="aneel:collection.guide.suspended" />
      </div>
    </section>
  );
}
